import React from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import WorkFlowImg from "../../assets/Logistics.jpg";
import {
  FaNetworkWired,
  FaShieldAlt,
  FaFileAlt,
  FaGlobe,
  FaMapMarkerAlt,
  FaChartLine,
} from "react-icons/fa";

const Workflow = () => {
  return (
    <>
      <Helmet>
        <title>Workflow | Trans World Shipping Company</title>
        <meta
          name="description"
          content="Learn about our streamlined workflow and how we ensure efficient logistics solutions for your business."
        />
        <link rel="canonical" href="https://transworldshipping.com/workflow" />
      </Helmet>

      <TheWorkFlow>
        <div className="work-hero">
          <img src={WorkFlowImg} alt="Logistics Workflow" />
          <div className="hero-overlay"></div>
          <div className="hero-text">
            <h1>Our Workflow</h1>
            <p>Streamlined processes for seamless logistics</p>
          </div>
        </div>

        <div className="workflow-container">
          <div className="success-section">
            <h2>Our Success Formula</h2>
            <div className="header-line"></div>
            <p className="success-text">
              Solid high security networking and state-of-the-art IT/IDS
              processing
            </p>
          </div>

          <div className="features-grid">
            <div className="feature-card">
              <FaGlobe className="feature-icon" />
              <h3>International Segments</h3>
              <p>Global reach across all major trade lanes</p>
            </div>

            <div className="feature-card">
              <FaShieldAlt className="feature-icon" />
              <h3>Secure Data Processing</h3>
              <p>Safe dataprocessing during daily online operations</p>
            </div>

            <div className="feature-card">
              <FaFileAlt className="feature-icon" />
              <h3>Digital Documentation</h3>
              <p>
                Permanent "online" documentation of all current and historical
                processes
              </p>
            </div>

            <div className="feature-card">
              <FaNetworkWired className="feature-icon" />
              <h3>Standardized Data Exchange</h3>
              <p>
                Data-exchange in XML/NRPC format following international
                standards
              </p>
            </div>

            <div className="feature-card">
              <FaMapMarkerAlt className="feature-icon" />
              <h3>Real-Time Tracking</h3>
              <p>Cargo tracking and status at all major ports worldwide</p>
            </div>

            <div className="feature-card">
              <FaChartLine className="feature-icon" />
              <h3>Performance Analytics</h3>
              <p>
                Continuous monitoring and optimization of logistics processes
              </p>
            </div>
          </div>

          <div className="workflow-benefits">
            <div className="benefit-item">
              <div className="benefit-number">01</div>
              <div className="benefit-content">
                <h4>Plan</h4>
                <p>Strategic planning and route optimization</p>
              </div>
            </div>
            <div className="benefit-item">
              <div className="benefit-number">02</div>
              <div className="benefit-content">
                <h4>Execute</h4>
                <p>Seamless execution with real-time monitoring</p>
              </div>
            </div>
            <div className="benefit-item">
              <div className="benefit-number">03</div>
              <div className="benefit-content">
                <h4>Track</h4>
                <p>End-to-end visibility and cargo tracking</p>
              </div>
            </div>
            <div className="benefit-item">
              <div className="benefit-number">04</div>
              <div className="benefit-content">
                <h4>Deliver</h4>
                <p>On-time delivery with complete documentation</p>
              </div>
            </div>
          </div>
        </div>
      </TheWorkFlow>
    </>
  );
};

const TheWorkFlow = styled.div`
  width: 100%;
  background: #ffffff;

  .work-hero {
    position: relative;
    width: 100%;
    height: 50vh;
    min-height: 400px;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .hero-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        135deg,
        rgba(11, 30, 51, 0.85) 0%,
        rgba(0, 0, 0, 0.6) 100%
      );
    }

    .hero-text {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      text-align: center;
      color: white;
      width: 100%;
      padding: 0 2rem;
      z-index: 1;

      h1 {
        font-size: 3rem;
        font-weight: 700;
        margin-bottom: 1rem;
      }

      p {
        font-size: 1.2rem;
        opacity: 0.9;
      }
    }
  }

  .workflow-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 4rem 2rem;
  }

  .success-section {
    text-align: center;
    margin-bottom: 3rem;

    h2 {
      font-size: 2rem;
      color: #0b1e33;
      margin-bottom: 1rem;
      font-weight: 700;
    }

    .header-line {
      width: 60px;
      height: 3px;
      background: #d4af37;
      margin: 0 auto 1.5rem;
    }

    .success-text {
      font-size: 1.1rem;
      color: #4a5568;
      max-width: 700px;
      margin: 0 auto;
      line-height: 1.6;
    }
  }

  .features-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 2rem;
    margin-bottom: 4rem;
  }

  .feature-card {
    background: #f8f9fa;
    padding: 2rem;
    border-radius: 12px;
    text-align: center;
    transition: all 0.3s ease;
    border: 1px solid #e2e8f0;

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
      border-color: #d4af37;
    }

    .feature-icon {
      font-size: 2.5rem;
      color: #d4af37;
      margin-bottom: 1rem;
    }

    h3 {
      font-size: 1.2rem;
      color: #0b1e33;
      margin-bottom: 0.5rem;
      font-weight: 700;
    }

    p {
      font-size: 0.9rem;
      color: #718096;
      line-height: 1.5;
    }
  }

  .workflow-benefits {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1.5rem;
    margin-top: 2rem;
  }

  .benefit-item {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    padding: 1.5rem;
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    border: 1px solid #e2e8f0;

    .benefit-number {
      font-size: 2rem;
      font-weight: 800;
      color: #d4af37;
      line-height: 1;
    }

    .benefit-content {
      flex: 1;

      h4 {
        font-size: 1.1rem;
        color: #0b1e33;
        margin-bottom: 0.25rem;
        font-weight: 700;
      }

      p {
        font-size: 0.85rem;
        color: #718096;
        line-height: 1.4;
      }
    }
  }

  @media screen and (max-width: 1200px) {
    .workflow-container {
      padding: 3rem 1.5rem;
    }
  }

  @media screen and (max-width: 1024px) {
    .features-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 1.5rem;
    }

    .workflow-benefits {
      grid-template-columns: repeat(2, 1fr);
      gap: 1rem;
    }
  }

  @media screen and (max-width: 900px) {
    .work-hero {
      height: 40vh;
      min-height: 350px;

      .hero-text h1 {
        font-size: 2.5rem;
      }

      .hero-text p {
        font-size: 1rem;
      }
    }

    .success-section h2 {
      font-size: 1.8rem;
    }
  }

  @media screen and (max-width: 768px) {
    .workflow-container {
      padding: 2rem 1.5rem;
    }

    .features-grid {
      grid-template-columns: 1fr;
      max-width: 450px;
      margin: 0 auto 3rem;
    }

    .workflow-benefits {
      grid-template-columns: 1fr;
      max-width: 450px;
      margin: 0 auto;
    }

    .benefit-item {
      padding: 1rem;
    }
  }

  @media screen and (max-width: 600px) {
    .work-hero {
      height: 35vh;
      min-height: 300px;

      .hero-text h1 {
        font-size: 2rem;
      }
    }

    .success-section {
      h2 {
        font-size: 1.5rem;
      }

      .success-text {
        font-size: 1rem;
      }
    }

    .feature-card {
      padding: 1.5rem;
    }
  }

  @media screen and (max-width: 480px) {
    .work-hero .hero-text h1 {
      font-size: 1.5rem;
    }

    .work-hero .hero-text p {
      font-size: 0.9rem;
    }

    .success-section h2 {
      font-size: 1.3rem;
    }
  }

  @media screen and (max-width: 350px) {
    .workflow-container {
      padding: 1.5rem 1rem;
    }

    .feature-card h3 {
      font-size: 1rem;
    }

    .benefit-item .benefit-number {
      font-size: 1.5rem;
    }
  }
`;

export default Workflow;
